import React, { useMemo } from 'react';
import { Alert, AlertTitle, Button, Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Horse } from '../../utils/api';
import { skipWhenOffline } from '../../offline';
import { inboxService } from '../../services/inboxService';
import { UnallocatedItem } from '../../types/inbox';

interface Props {
  /** The patient whose chart is open. Mount only for people who may see lab results. */
  horse: Horse;
  /** Minimum match score for an item to surface here. Default 50. */
  threshold?: number;
}

/**
 * Surfaces on a horse profile when a pending inbox item matches *this* horse. Gives a one-click
 * jump to the Inbox so the clinician can confirm the assignment before it shows up as a real result
 * on the patient's timeline.
 *
 * It scores against the open patient only (fix 5): it used to download every patient in the clinic
 * to find each item's best match. So two patients who both match an item both show it.
 *
 * Renders nothing when there are no candidates — completely silent on the happy path.
 */
const UnallocatedSuggestionBanner: React.FC<Props> = ({ horse, threshold = 50 }) => {
  const { data: items = [] } = useQuery<UnallocatedItem[]>(
    ['inbox', 'PENDING'],
    () => inboxService.list('PENDING'),
    { enabled: skipWhenOffline(), staleTime: 60_000 },
  );

  const candidates = useMemo(
    () => items.filter(item => (inboxService.scoreMatches(item, [horse])[0]?.score ?? 0) >= threshold),
    [items, horse, threshold],
  );

  if (candidates.length === 0) return null;

  return (
    <Alert
      severity="info"
      variant="outlined"
      sx={{ mb: 1.5 }}
      action={
        <Button
          size="small"
          component={RouterLink}
          to="/clinical-records/unallocated"
          variant="contained"
          color="primary"
        >
          Review unallocated
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0 }}>
        {candidates.length === 1
          ? '1 result may belong to this patient'
          : `${candidates.length} results may belong to this patient`}
      </AlertTitle>
      <Box mt={0.5}>
        {candidates.slice(0, 3).map(c => (
          <Typography key={c.id} variant="caption" display="block" color="text.secondary">
            {c.source} · {c.kind === 'imaging_study' ? c.modality ?? 'Imaging' : c.panelName ?? 'Lab'}
            {c.patientNameOnReport ? ` — listed as "${c.patientNameOnReport}"` : ''}
          </Typography>
        ))}
        {candidates.length > 3 && (
          <Typography variant="caption" color="text.secondary">
            +{candidates.length - 3} more
          </Typography>
        )}
      </Box>
    </Alert>
  );
};

export default UnallocatedSuggestionBanner;
